import { Component, OnInit, Inject, ViewChild, AfterViewInit, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { FileUploadComponent } from 'src/app/shared/components/file-upload/file-upload.component';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';

interface PurchaseRequest {
  prNumber: string;
  prName: string;
}

@Component({
  selector: 'app-create-agenda-item-dialog',
  templateUrl: './create-agenda-item-dialog.component.html',
  styleUrls: ['./create-agenda-item-dialog.component.scss']
})
export class CreateAgendaItemDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('fileUploadComponent') fileUploadComponent!: FileUploadComponent;
  @ViewChild('tcDecisionEnEditor') tcDecisionEnEditor?: ElementRef<HTMLDivElement>;
  @ViewChild('tcDecisionArEditor') tcDecisionArEditor?: ElementRef<HTMLDivElement>;
  
  agendaForm: FormGroup;
  currentStages: string[] = [
    'Market Tender',
    'Unsealing Combined Documents',
    'Unsealing Technical Documents',
    'Unsealing Commercial Documents',
    'Evaluation Review',
    'Award Bid',
  ];
  departments: string[] = [];
  purchaseRequests: PurchaseRequest[] = [];
  purchaseRequestOptions: string[] = [];
  selectedPRs: string[] = [];

  isTranslating: boolean = false; // Flag to prevent translation loop
  isEditMode: boolean = false;
  isViewMode: boolean = false;
  prNumber: string | null = null;
  agendaItemId: number | null = null;
  meetingId: number | null = null; // Meeting ID to link agenda item to meeting

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CreateAgendaItemDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private toasterService: ToasterService,
    private translationService: TranslationService,
    private http: HttpClient
  ) {
    // Check if we're in edit mode or view mode
    this.isEditMode = data?.editMode || false;
    this.isViewMode = data?.viewMode || false;
    this.prNumber = data?.prNumber || null;
    this.agendaItemId = data?.agendaItemId || null;
    this.meetingId = data?.meetingId || null; // Get meeting ID if provided
    
    this.agendaForm = this.fb.group({
      prIdentifiers: [this.prNumber || ''], // Link PR - text input field
      agendaTitleAR: ['', Validators.required],
      agendaTitleEN: ['', Validators.required],
      subject: ['', Validators.required],
      department: [''],
      currentStage: [''],
      agendaDescriptionEN: [''],
      agendaDescriptionAR: [''],
      requiredFromTC: [''],
      tcNotes: [''],
      tcDecisionAR: [''], // TC Decision AR
      tcDecisionEN: [''],  // TC Decision EN
      attachments: [null]  // Attachments FormControl for file-upload component
    });

    // Disable all form controls in view mode
    if (this.isViewMode) {
      this.agendaForm.disable();
    }
  }

  async onPRSelectionChange(selectedOption: string): Promise<void> {
    const selectedValue = this.getPRNumberFromOption(selectedOption);
    const selectedDisplay = selectedValue ? this.getPRDisplayValue(selectedValue) : '';
    
    this.agendaForm.patchValue({ prIdentifiers: selectedDisplay }, { emitEvent: false });
    this.selectedPRs = selectedValue ? [selectedValue] : [];
    
    // Update subject based on selected PR if subject is empty
    if (selectedValue && !this.agendaForm.value.subject) {
      const selectedPR = this.purchaseRequests.find(pr => pr.prNumber === selectedValue);
      const subject = selectedPR ? `${selectedPR.prNumber} - ${selectedPR.prName}` : `${selectedValue} - Review`;
      this.agendaForm.patchValue({ 
        subject: subject,
        agendaTitleEN: subject
      });
    }
  }

  private getPRDisplayValue(prNumber: string): string {
    const selectedPR = this.purchaseRequests.find(pr => pr.prNumber === prNumber);
    return selectedPR ? `${selectedPR.prNumber} - ${selectedPR.prName}` : prNumber;
  }

  private getPRNumberFromOption(option: string): string {
    if (!option) return '';

    const selectedPR = this.purchaseRequests.find(pr =>
      option === pr.prNumber || option === `${pr.prNumber} - ${pr.prName}`
    );

    return selectedPR?.prNumber || option.split(' - ')[0]?.trim() || '';
  }
  async loadTCDecisionsForPR(prNumber: string): Promise<void> {
    try {
      // Fetch all agenda items
      const url = `${environment.API_URL}/api/AgendaItems`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      
      if (response?.isSuccess && response?.data) {
        // Find agenda items with this PR number
        const agendaItemsWithPR = response.data.filter((item: any) => 
          item.prIdentifiers && item.prIdentifiers.includes(prNumber)
        );

        // Find the first agenda item with TC Decisions
        const agendaItemWithTCDecisions = agendaItemsWithPR.find((item: any) => 
          item.tcDecisionAR || item.tcDecisionEN
        );

        // Auto-populate TC Decisions if found and fields are empty
        if (agendaItemWithTCDecisions) {
          const currentTCDecisionEN = this.agendaForm.get('tcDecisionEN')?.value || '';
          const currentTCDecisionAR = this.agendaForm.get('tcDecisionAR')?.value || '';

          if (!currentTCDecisionEN && agendaItemWithTCDecisions.tcDecisionEN) {
            this.agendaForm.patchValue({ tcDecisionEN: agendaItemWithTCDecisions.tcDecisionEN });
          }

          if (!currentTCDecisionAR && agendaItemWithTCDecisions.tcDecisionAR) {
            this.agendaForm.patchValue({ tcDecisionAR: agendaItemWithTCDecisions.tcDecisionAR });
          }

          this.syncEditorViews();
        }
      }
    } catch (error) {
      console.error('Error loading TC Decisions for PR:', error);
      // Silently fail - this is just for auto-population
    }
  }

  async ngOnInit(): Promise<void> {
    // Always load supporting dropdown data first
    await Promise.all([
      this.loadPurchaseRequests(),
      this.loadDepartments()
    ]);
    
    // If in edit mode or view mode, load agenda item details after PRs are loaded
    if ((this.isEditMode || this.isViewMode) && this.agendaItemId) {
      await this.loadAgendaItemDetails();
    }
  }

  async ngAfterViewInit(): Promise<void> {
    this.syncEditorViews();
    // After view is initialized, load attachments if in edit/view mode
    // This ensures fileUploadComponent is available
    if ((this.isEditMode || this.isViewMode) && this.agendaItemId && this.fileUploadComponent) {
      // Small delay to ensure component is fully initialized
      setTimeout(async () => {
        await this.loadAgendaItemAttachments();
      }, 100);
    }
  }
  
  async loadAgendaItemDetails(): Promise<void> {
    try {
      this._loaderService.showLoader();
      // Fetch the agenda item details
      const url = `${environment.API_URL}/api/AgendaItems/GetAgendaItemDetails/${this.agendaItemId}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      
      if (response?.isSuccess && response?.data) {
        const agendaItem = response.data;
        
        // Populate form with agenda item data
        // Note: API returns PascalCase properties, handle both camelCase and PascalCase
        const prIds = agendaItem.prIdentifiers || agendaItem.PRIdentifiers;
        const firstPR = prIds && Array.isArray(prIds) && prIds.length > 0 ? prIds[0] : '';
        
        // Explicitly check for Agenda Description fields - these may not exist in backend yet
        // Ensure they are always empty strings if not present in API response
        const agendaDescEN = (agendaItem.agendaDescriptionEN || agendaItem.AgendaDescriptionEN || '').toString().trim();
        const agendaDescAR = (agendaItem.agendaDescriptionAR || agendaItem.AgendaDescriptionAR || '').toString().trim();
        
        this.agendaForm.patchValue({
          prIdentifiers: this.getPRDisplayValue(firstPR),
          agendaTitleAR: agendaItem.agendaTitleAR || agendaItem.AgendaTitleAR || '',
          agendaTitleEN: agendaItem.agendaTitleEN || agendaItem.AgendaTitleEN || '',
          subject: agendaItem.subject || agendaItem.Subject || '',
          department: agendaItem.department || agendaItem.Department || '',
          currentStage: agendaItem.currentStage || agendaItem.CurrentStage || '',
          // Agenda Description fields - explicitly set to empty if not present
          agendaDescriptionEN: agendaDescEN || '',
          agendaDescriptionAR: agendaDescAR || '',
          requiredFromTC: agendaItem.requiredFromTC || agendaItem.RequiredFromTC || '',
          tcNotes: agendaItem.tcNotes || agendaItem.TCNotes || '',
          tcDecisionAR: agendaItem.tcDecisionAR || agendaItem.TCDecisionAR || '',
          tcDecisionEN: agendaItem.tcDecisionEN || agendaItem.TCDecisionEN || ''
        });
        
        this.syncEditorViews();

        // Update prNumber if not already set
        if (firstPR) {
          this.prNumber = firstPR;
          this.selectedPRs = [firstPR];
        }
      }
    } catch (error) {
      console.error('Error loading agenda item details:', error);
      this.toasterService.showError('Failed to load agenda item details. Please try again.', 'Error');
    } finally {
      this._loaderService.hideLoader();
    }
  }

  async loadAgendaItemAttachments(): Promise<void> {
    if (!this.agendaItemId || !this.fileUploadComponent) {
      return;
    }

    try {
      // Fetch documents for this agenda item
      const documentsUrl = `${environment.API_URL}/api/Documents/GetDocuments`;
      const documentsDto = {
        applicationId: this.agendaItemId.toString(),
        processName: 'AgendaItem',
        processID: this.agendaItemId.toString()
      };

      const documentsResponse: any = await lastValueFrom(this.apiService.postByUrl(documentsUrl, documentsDto));

      // Handle both array response and string response ("No documents found")
      if (documentsResponse?.isSuccess && documentsResponse?.data) {
        // Check if data is an array (documents) or string (no documents message)
        if (Array.isArray(documentsResponse.data) && documentsResponse.data.length > 0) {
          const documents = documentsResponse.data;

          // Fetch all files first, then add them to the component
          // Option 1: Direct blob access (requires CORS configured on Azure Blob Storage) - RECOMMENDED
          // Option 2: Backend proxy (fallback if CORS not configured)
          const USE_DIRECT_BLOB_ACCESS = true; // Set to false to use backend proxy
          
          const filePromises = documents
            .filter((doc: any) => {
              if (USE_DIRECT_BLOB_ACCESS) {
                return doc.fileContent && doc.name; // Need fileContent (SAS URL) for direct access
              } else {
                return doc.attachmentId && doc.name; // Need attachmentId for backend proxy
              }
            })
            .map(async (doc: any) => {
              try {
                let blob: Blob;
                let contentType: string;
                
                if (USE_DIRECT_BLOB_ACCESS) {
                  // DIRECT ACCESS: Fetch directly from Azure Blob Storage (requires CORS)
                  // This is the BEST PRACTICE approach - faster, more scalable
                  const fileUrl = doc.fileContent; // This is the SAS URL from backend
                  
                  const response = await fetch(fileUrl, {
                    method: 'GET',
                    headers: {
                      'Origin': window.location.origin // Required for CORS
                    }
                  });
                  
                  if (!response.ok) {
                    console.error(`Failed to fetch file from URL: ${fileUrl}`, response.statusText);
                    return null;
                  }
                  
                  blob = await response.blob();
                  contentType = doc.mimeType || blob.type || 'application/octet-stream';
                } else {
                  // BACKEND PROXY: Use backend endpoint (fallback if CORS not configured)
                  const downloadUrl = `${environment.API_URL}/api/Documents/DownloadDocument/${doc.attachmentId}`;
                  
                  const response = await this.http.get(downloadUrl, { 
                    responseType: 'blob',
                    observe: 'response'
                  }).toPromise();
                  
                  if (!response || !response.body) {
                    console.error(`Failed to fetch file for attachment ${doc.attachmentId}`);
                    return null;
                  }
                  
                  blob = response.body;
                  contentType = response.headers.get('content-type') || doc.mimeType || 'application/octet-stream';
                }
                
                // Create a File object from the blob with proper name and type
                const file = new File([blob], doc.name, { 
                  type: contentType,
                  lastModified: Date.now()
                });

                return {
                  fileName: doc.name,
                  file: file,
                  fileType: contentType
                };
              } catch (error: any) {
                // If direct access fails with CORS error, log helpful message
                if (USE_DIRECT_BLOB_ACCESS && error?.message?.includes('CORS')) {
                  console.error(`CORS error loading attachment ${doc.name}. Please configure CORS on Azure Blob Storage or set USE_DIRECT_BLOB_ACCESS to false.`, error);
                } else {
                  console.error(`Error loading attachment ${doc.name}:`, error);
                }
                return null;
              }
            });

          // Wait for all files to be fetched
          const fileResults = await Promise.all(filePromises);
          
          // Filter out any failed fetches and add files to the component
          const validFiles = fileResults.filter(result => result !== null);
          
          for (const fileResult of validFiles) {
            if (fileResult) {
              // Add file to the file-upload component
              // pushFileToUploadedFiles expects Blob, but File extends Blob so this works
              this.fileUploadComponent.pushFileToUploadedFiles(
                fileResult.fileName, 
                fileResult.file, 
                fileResult.fileType
              );
            }
          }
        }
        // If data is a string, it means "No documents found" - do nothing
      }
    } catch (error) {
      console.error('Error loading agenda item attachments:', error);
      // Don't show error to user - attachments are optional
    }
  }

  async onTranslateToArabic(): Promise<void> {
    const englishHtml = this.agendaForm.get('tcDecisionEN')?.value || '';
    const englishText = this.getPlainTextFromHtml(englishHtml);
    if (!englishText || englishText.trim() === '') {
      this.toasterService.showWarning('Please enter text in English to translate', 'No Text');
      return;
    }

    this.isTranslating = true;
    try {
      const translatedHtml = await this.translateEditorHtml(englishHtml);
      this.agendaForm.patchValue({ tcDecisionAR: translatedHtml }, { emitEvent: false });
      this.syncEditorViews();
      this.toasterService.showSuccess('Text translated successfully', 'Translation Complete');
    } catch (error) {
      console.error('Translation failed:', error);
      this.toasterService.showError('Translation failed. Please try again.', 'Translation Error');
    } finally {
      this.isTranslating = false;
    }
  }

  async loadPurchaseRequests(): Promise<void> {
    try {
      const url = `${environment.API_URL}/api/PurchaseRequests`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      
      if (response?.isSuccess && response?.data) {
        this.purchaseRequests = response.data.map((pr: any) => ({
          prNumber: pr.prNumber,
          prName: pr.prName || pr.prNumber
        }));
        this.purchaseRequestOptions = this.purchaseRequests.map(pr => `${pr.prNumber} - ${pr.prName}`);

        if (this.prNumber && !this.isEditMode && !this.isViewMode) {
          this.selectedPRs = [this.prNumber];
          this.agendaForm.patchValue({ prIdentifiers: this.getPRDisplayValue(this.prNumber) }, { emitEvent: false });
        }
      } else {
        console.error('Failed to load purchase requests:', response?.errorMessageEn);
        this.purchaseRequests = [];
        this.purchaseRequestOptions = [];
      }
    } catch (error) {
      console.error('Error loading purchase requests:', error);
      this.purchaseRequests = [];
      this.purchaseRequestOptions = [];
    }
  }

  async loadDepartments(): Promise<void> {
    try {
      const url = `${environment.API_URL}/api/Lookups/departments`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      const rawData = Array.isArray(response) ? response : (response?.data || response?.Data || []);

      this.departments = Array.isArray(rawData)
        ? rawData
            .map((department: any) => (department.departmentName || department.DepartmentName || '').toString().trim())
            .filter((departmentName: string) => departmentName !== '')
        : [];
    } catch (error) {
      console.error('Error loading departments:', error);
      this.departments = [];
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  async onAddAnother(): Promise<void> {
    if (this.agendaForm.valid) {
      await this.saveAgendaItem();
      // Reset form for another entry
      this.agendaForm.patchValue({
        prIdentifiers: '',
        agendaTitleAR: '',
        agendaTitleEN: '',
        subject: '',
        department: '',
        currentStage: '',
        agendaDescriptionEN: '',
        agendaDescriptionAR: '',
        requiredFromTC: '',
        tcNotes: '',
        tcDecisionAR: '',
        tcDecisionEN: '',
        attachments: null
      });
      this.syncEditorViews();
      this.selectedPRs = [];
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.agendaForm.controls).forEach(key => {
        this.agendaForm.get(key)?.markAsTouched();
      });
    }
  }

  async onSave(): Promise<void> {
    if (this.agendaForm.valid) {
      await this.saveAgendaItem();
      this.dialogRef.close(true);
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.agendaForm.controls).forEach(key => {
        this.agendaForm.get(key)?.markAsTouched();
      });
    }
  }

  executeEditorCommand(
    editor: HTMLDivElement,
    controlName: 'tcDecisionEN' | 'tcDecisionAR',
    command: string,
    value?: string
  ): void {
    if (this.isViewMode) {
      return;
    }

    editor.focus();
    document.execCommand(command, false, value);
    this.updateEditorFormControl(controlName, editor);
  }

  onEditorInput(controlName: 'tcDecisionEN' | 'tcDecisionAR', editor: HTMLDivElement): void {
    this.updateEditorFormControl(controlName, editor);
  }

  onEditorPaste(event: ClipboardEvent, controlName: 'tcDecisionEN' | 'tcDecisionAR', editor: HTMLDivElement): void {
    if (this.isViewMode) {
      return;
    }

    const pastedText = event.clipboardData?.getData('text/plain');

    if (!pastedText) {
      return;
    }

    event.preventDefault();
    editor.focus();
    document.execCommand('insertText', false, this.normalizePastedPlainText(pastedText));
    this.updateEditorFormControl(controlName, editor);
  }

  onEditorBlur(controlName: 'tcDecisionEN' | 'tcDecisionAR'): void {
    this.agendaForm.get(controlName)?.markAsTouched();
  }

  hasEditorText(controlName: 'tcDecisionEN' | 'tcDecisionAR'): boolean {
    return this.getEditorPlainText(controlName).length > 0;
  }

  isEditorEmpty(controlName: 'tcDecisionEN' | 'tcDecisionAR'): boolean {
    return !this.hasEditorText(controlName);
  }

  private updateEditorFormControl(controlName: 'tcDecisionEN' | 'tcDecisionAR', editor: HTMLDivElement): void {
    const normalizedHtml = this.normalizeEditorHtml(editor.innerHTML);
    this.agendaForm.get(controlName)?.setValue(normalizedHtml);
    this.agendaForm.get(controlName)?.markAsDirty();
  }

  private syncEditorViews(): void {
    this.setEditorContent(this.tcDecisionEnEditor, this.agendaForm.get('tcDecisionEN')?.value || '');
    this.setEditorContent(this.tcDecisionArEditor, this.agendaForm.get('tcDecisionAR')?.value || '');
  }

  private setEditorContent(editorRef: ElementRef<HTMLDivElement> | undefined, html: string): void {
    if (!editorRef?.nativeElement) {
      return;
    }

    if (editorRef.nativeElement.innerHTML !== html) {
      editorRef.nativeElement.innerHTML = html;
    }
  }

  private getEditorPlainText(controlName: 'tcDecisionEN' | 'tcDecisionAR'): string {
    const html = this.agendaForm.get(controlName)?.value || '';
    return this.getPlainTextFromHtml(html);
  }

  private normalizeEditorHtml(html: string): string {
    return this.getPlainTextFromHtml(html) ? html : '';
  }

  private normalizePastedPlainText(text: string): string {
    return text
      .replace(/\r\n?/g, '\n')
      .replace(/\u00a0/g, ' ')
      .replace(/\t/g, ' ')
      .split('\n')
      .map(line => line.replace(/[ ]{2,}/g, ' ').trimEnd())
      .join('\n')
      .trim();
  }

  private getPlainTextFromHtml(html: string): string {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = html;
    return (tempElement.textContent || tempElement.innerText || '').replace(/\u00a0/g, ' ').trim();
  }

  private async translateEditorHtml(html: string): Promise<string> {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    const textNodes: Text[] = [];
    const walker = document.createTreeWalker(wrapper, NodeFilter.SHOW_TEXT);
    let currentNode = walker.nextNode();

    while (currentNode) {
      const textNode = currentNode as Text;
      if (textNode.textContent && textNode.textContent.trim()) {
        textNodes.push(textNode);
      }
      currentNode = walker.nextNode();
    }

    for (const textNode of textNodes) {
      textNode.textContent = await this.translateEditorTextNode(textNode.textContent || '');
    }

    return this.normalizeEditorHtml(wrapper.innerHTML);
  }

  private async translateEditorTextNode(text: string): Promise<string> {
    const parts = text.split(/(\n+)/);
    const translatedParts: string[] = [];

    for (const part of parts) {
      if (!part.trim()) {
        translatedParts.push(part);
        continue;
      }

      const leadingWhitespace = part.match(/^\s*/)?.[0] || '';
      const trailingWhitespace = part.match(/\s*$/)?.[0] || '';
      const translatedText = this.cleanTranslatedText(
        await lastValueFrom(this.translationService.translateToArabic(part.trim()))
      );

      translatedParts.push(`${leadingWhitespace}${translatedText}${trailingWhitespace}`);
    }

    return translatedParts.join('');
  }

  private cleanTranslatedText(text: string): string {
    const decoder = document.createElement('textarea');
    decoder.innerHTML = text || '';
    return decoder.value
      .replace(/<\/?g\b[^>]*>/gi, '')
      .replace(/<\/?x\b[^>]*>/gi, '')
      .replace(/<[^>]+>/g, '')
      .trim();
  }

  private async saveAgendaItem(): Promise<void> {
    this._loaderService.showLoader();
    try {
      const formData = this.agendaForm.value;
      
      // Get actual File objects from the file-upload component
      const uploadedFiles: File[] = this.fileUploadComponent?.getUploadedFiles() || [];
      const hasFiles = uploadedFiles.length > 0;

      const agendaItemData: any = {
        agendaTitleAR: formData.agendaTitleAR,
        agendaTitleEN: formData.agendaTitleEN,
        subject: formData.subject,
        department: formData.department || null,
        currentStage: formData.currentStage || null,
        agendaDescriptionEN: formData.agendaDescriptionEN || null,
        agendaDescriptionAR: formData.agendaDescriptionAR || null,
        requiredFromTC: formData.requiredFromTC || null,
        tcNotes: formData.tcNotes || null,
        tcDecisionAR: formData.tcDecisionAR || null,
        tcDecisionEN: formData.tcDecisionEN || null,
        prIdentifiers: this.selectedPRs.length > 0 ? this.selectedPRs : (formData.prIdentifiers ? [this.getPRNumberFromOption(formData.prIdentifiers)] : []),
        meetingId: this.meetingId || null
      };

      let response: any;

      // If files are present, use FormData (multipart/form-data) via ApiService
      if (hasFiles) {
        // Add processName to payload for file attachment type
        const payloadWithProcessName = {
          ...agendaItemData,
          processName: 'AgendaItem'
        };

        const url = this.isEditMode && this.agendaItemId
          ? `${environment.API_URL}/api/AgendaItems/${this.agendaItemId}`
          : `${environment.API_URL}/api/AgendaItems`;

        // Use ApiService multipart methods which handle headers automatically
        if (this.isEditMode && this.agendaItemId) {
          response = await lastValueFrom(this.apiService.putMultipartByUrl(url, payloadWithProcessName, uploadedFiles));
        } else {
          response = await lastValueFrom(this.apiService.postMultipartByUrl(url, payloadWithProcessName, uploadedFiles));
        }
      } else {
        // No files, use regular JSON request
        const url = this.isEditMode && this.agendaItemId
          ? `${environment.API_URL}/api/AgendaItems/${this.agendaItemId}`
          : `${environment.API_URL}/api/AgendaItems`;
        
        response = this.isEditMode && this.agendaItemId
          ? await lastValueFrom(this.apiService.putByUrl(url, agendaItemData))
          : await lastValueFrom(this.apiService.postByUrl(url, agendaItemData));
      }

      if (response?.isSuccess) {
        // Success - show success message
        const message = this.isEditMode ? 'Agenda item updated successfully!' : 'Agenda item created successfully!';
        this.toasterService.showSuccess(message);
      } else {
        console.error(`Failed to ${this.isEditMode ? 'update' : 'create'} agenda item:`, response?.errorMessageEn);
        const errorMessage = response?.errorMessageEn || `Failed to ${this.isEditMode ? 'update' : 'create'} agenda item. Please try again.`;
        this.toasterService.showError(errorMessage);
      }
    } catch (error: any) {
      console.error('Error creating agenda item:', error);
      this.toasterService.showError(error?.error?.errorMessageEn || 'An error occurred while creating the agenda item. Please try again.');
    } finally {
      this._loaderService.hideLoader();
    }
  }
}
